import { useEffect, useMemo, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowForward,
  CheckCircle,
  DeleteOutline,
  Edit,
  GroupsOutlined,
  LockOutlined,
  PersonOutline,
  Shield,
  VisibilityOutlined,
  WorkspacePremiumOutlined,
} from '@mui/icons-material';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useProduct } from '@/auth/ProductContext';
import { toApiError } from '@/api/client';
import { isUsable, listProductSubscriptions, type ProductSubscription } from '@/api/products';
import * as rbac from '@/api/rbac';
import type { CustomRole, Permission } from '@/api/rbac';
import type { TildeProduct } from '@/components/primitives/products';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { commercialProductLabelFromTechnical } from '@/lib/commercialProducts';

export const Route = createFileRoute('/_authenticated/roles/')({ component: RolesPage });

type CommercialProduct = Exclude<TildeProduct, 'Admin'>;
type AccessProfile = 'administrator' | 'operator' | 'viewer';
type Toast = { message: string; severity: 'success' | 'error' };

export interface Capability {
  permission: Permission;
  label: string;
  description: string;
  readOnly?: boolean;
}

interface ProfileDefinition {
  key: AccessProfile;
  name: string;
  forWhom: string;
  description: string;
  icon: JSX.Element;
  accent: 'primary' | 'success' | 'info';
}

const PRODUCT_COPY: Record<CommercialProduct, { eyebrow: string; title: string; description: string }> = {
  Empresa: {
    eyebrow: 'FACTURACIÓN PARA TU ACTIVIDAD',
    title: 'Facturación',
    description: 'Separá la emisión, la cobranza y la administración sin compartir accesos innecesarios.',
  },
  Estudio: {
    eyebrow: 'EQUIPO DEL ESTUDIO',
    title: 'Estudio Contable',
    description: 'Asigná responsabilidades y, si tu plan lo permite, limitá a cada colaborador a sus CUITs.',
  },
  Api: {
    eyebrow: 'EQUIPO TÉCNICO',
    title: 'API para Programadores',
    description: 'Diferenciá quién desarrolla, quién administra credenciales y quién controla la operación.',
  },
};

const FEATURE_LABEL: Record<string, string> = {
  'api-credentials': 'Credenciales API',
  'account-manager': 'Account manager',
  'approval-workflows': 'Flujos de aprobación',
  'audit-export': 'Exportación de auditoría',
  'certificate-center': 'Centro de certificados',
  'client-portal': 'Portal de clientes',
  'co-branding': 'Marca del estudio',
  'custom-roles': 'Roles a medida',
  'email-delivery': 'Envío por email',
  'future-increases': 'Incrementos programados',
  'ip-allowlist': 'Restricción por IP',
  'latency-metrics': 'Métricas de latencia',
  'multi-user': 'Trabajo en equipo',
  'outbound-webhooks': 'Webhooks',
  'per-cuit-permissions': 'Permisos por CUIT',
  'portfolio-reports': 'Reportes de cartera',
  'priority-support': 'Soporte prioritario',
  'qr-afip': 'QR ARCA',
  'recurring-billing': 'Facturación recurrente',
  'webhook-retries': 'Reintentos de webhooks',
  sso: 'Inicio de sesión empresarial',
  'sla-99-9': 'SLA 99,9%',
  'study-clients': 'Clientes del estudio',
  'white-label': 'White-label',
};

const CAPABILITIES: Record<CommercialProduct, Capability[]> = {
  Empresa: [
    { permission: 'ViewInvoicesAndClients', label: 'Consultar comprobantes y receptores', description: 'Ve el historial y los datos de facturación.', readOnly: true },
    { permission: 'EmitInvoices', label: 'Emitir comprobantes', description: 'Genera facturas, notas de crédito y débito.' },
    { permission: 'VoidOrCreditNote', label: 'Corregir comprobantes', description: 'Emite notas de crédito o débito cuando corresponde.' },
    { permission: 'ManageRecurring', label: 'Administrar facturación recurrente', description: 'Programa cobros mensuales e incrementos.' },
    { permission: 'ManageCollections', label: 'Gestionar cobranzas', description: 'Registra y da seguimiento a los pagos.' },
    { permission: 'ViewReports', label: 'Ver reportes', description: 'Consulta indicadores de la operación.', readOnly: true },
    { permission: 'ExportAudit', label: 'Revisar auditoría', description: 'Consulta la trazabilidad de las acciones.', readOnly: true },
  ],
  Estudio: [
    { permission: 'ViewClientsAndCuits', label: 'Consultar clientes y CUITs', description: 'Ve la cartera que tiene asignada.', readOnly: true },
    { permission: 'ManageClients', label: 'Gestionar clientes del estudio', description: 'Mantiene datos y responsables de cada cliente.' },
    { permission: 'ManageCertificates', label: 'Administrar certificados ARCA', description: 'Carga, valida y controla vencimientos.' },
    { permission: 'OperateRooms', label: 'Operar salas de habilitación', description: 'Acompaña el alta y la vinculación con ARCA.' },
    { permission: 'EmitInvoices', label: 'Emitir para CUITs autorizados', description: 'Factura únicamente en la cartera habilitada.' },
    { permission: 'ManageFees', label: 'Gestionar honorarios', description: 'Programa honorarios y ajustes del estudio.' },
    { permission: 'ManagePerCuitPermissions', label: 'Asignar CUITs por colaborador', description: 'Evita que una persona vea clientes ajenos.' },
    { permission: 'ViewReports', label: 'Ver reportes de cartera', description: 'Consulta el estado general del estudio.', readOnly: true },
    { permission: 'ExportAudit', label: 'Revisar auditoría', description: 'Controla quién hizo cada acción.', readOnly: true },
  ],
  Api: [
    { permission: 'ViewAppsAndLogs', label: 'Consultar aplicaciones y logs', description: 'Ve actividad, errores y consumo.', readOnly: true },
    { permission: 'ManageApps', label: 'Administrar aplicaciones', description: 'Crea y configura integraciones.' },
    { permission: 'ManageCredentials', label: 'Administrar credenciales', description: 'Genera y rota secretos de acceso.' },
    { permission: 'ManageWebhooks', label: 'Configurar webhooks', description: 'Define destinos y reintentos.' },
    { permission: 'AccessSandbox', label: 'Usar homologación', description: 'Prueba integraciones sin emitir en producción.' },
    { permission: 'AccessProduction', label: 'Usar producción', description: 'Opera con credenciales y CUITs productivos.' },
  ],
};

const PROFILE_DEFINITIONS: ProfileDefinition[] = [
  {
    key: 'administrator',
    name: 'Administrador',
    forWhom: 'Dueño o responsable',
    description: 'Configura el producto, gestiona el equipo y puede operar todas las funciones incluidas.',
    icon: <Shield />,
    accent: 'primary',
  },
  {
    key: 'operator',
    name: 'Operador',
    forWhom: 'Colaborador operativo',
    description: 'Trabaja en la operación diaria sin poder cambiar el plan ni los accesos del equipo.',
    icon: <PersonOutline />,
    accent: 'success',
  },
  {
    key: 'viewer',
    name: 'Solo lectura',
    forWhom: 'Auditor o consulta',
    description: 'Puede consultar información y reportes, pero no modificar ni emitir.',
    icon: <VisibilityOutlined />,
    accent: 'info',
  },
];

export function commercialProductName(product: CommercialProduct, tier: string | undefined): string {
  if (product === 'Empresa') return commercialProductLabelFromTechnical(product, tier);
  return PRODUCT_COPY[product].title;
}

function limitLabel(key: string, value: number): string | null {
  if (value === 0) return null;
  const amount = value === -1 ? 'Ilimitados' : value.toLocaleString('es-AR');
  return {
    'max-apps': `${amount} app${value === 1 ? '' : 's'}`,
    'max-cuit-profiles': `${amount} CUIT${value === 1 ? '' : 's'}`,
    'max-enablement-rooms': `${amount} salas de habilitación`,
    'max-invoices-per-month': `${amount} comprobantes/mes`,
    'max-operations-per-month': `${amount} operaciones/mes`,
    'max-points-of-sale': `${amount} punto${value === 1 ? '' : 's'} de venta`,
    'max-users': `${amount} usuario${value === 1 ? '' : 's'}`,
    'log-retention-days': `${amount} días de logs`,
    'sandbox-requests-per-month': `${amount} consultas de prueba/mes`,
  }[key] ?? null;
}

export function visibleCapabilities(
  product: CommercialProduct,
  subscription: Pick<ProductSubscription, 'tier' | 'features'> | undefined,
): Capability[] {
  const features = new Set(subscription?.features ?? []);
  return CAPABILITIES[product].filter(({ permission }) => {
    if (permission === 'ManageRecurring') return features.has('recurring-billing');
    if (permission === 'ManagePerCuitPermissions') return features.has('per-cuit-permissions');
    if (permission === 'ViewReports' && product === 'Estudio') return features.has('portfolio-reports');
    if (permission === 'ManageCertificates' || permission === 'OperateRooms') return features.has('certificate-center');
    if (permission === 'ManageCredentials') return features.has('api-credentials');
    if (permission === 'ManageWebhooks') return features.has('outbound-webhooks');
    if (permission === 'AccessProduction') return subscription?.tier.toLowerCase() !== 'sandbox';
    return true;
  });
}

function profileHasAccess(profile: AccessProfile, capability: Capability): 'full' | 'read' | 'none' {
  if (profile === 'administrator') return 'full';
  if (profile === 'viewer') return capability.readOnly ? 'read' : 'none';
  if (['ExportAudit', 'ManagePerCuitPermissions'].includes(capability.permission)) return 'none';
  return capability.readOnly ? 'read' : 'full';
}

function RolesPage(): JSX.Element {
  const { activeProduct } = useProduct();
  const [section, setSection] = useState<'profiles' | 'custom'>('profiles');
  const [toast, setToast] = useState<Toast | null>(null);
  const subscriptionsQ = useQuery({ queryKey: ['product-subscriptions'], queryFn: listProductSubscriptions });

  const product: CommercialProduct = activeProduct === 'Admin' ? 'Empresa' : activeProduct;
  const subscription = subscriptionsQ.data?.find(
    (item) => item.product === product && isUsable(item),
  );
  const displayProduct = commercialProductName(product, subscription?.tier);
  // Compatibilidad con tenants previos al modelo multi-producto: ProductContext ya los resuelve
  // como la línea técnica Empresa presentada comercialmente como Monotributo, por lo que la
  // ausencia de attachment conserva el comportamiento individual.
  const isSingleUser = subscription
    ? subscription.limits['max-users'] === 1
    : product === 'Empresa';
  const supportsCustomRoles = subscription?.features.includes('custom-roles') ?? false;
  const capabilities = useMemo(() => visibleCapabilities(product, subscription), [product, subscription]);

  useEffect(() => {
    if (!supportsCustomRoles && section === 'custom') setSection('profiles');
  }, [section, supportsCustomRoles]);

  if (subscriptionsQ.isLoading) return <LoadingScreen message="Cargando accesos…" />;
  if (subscriptionsQ.error) return <MuiAlert severity="error">{toApiError(subscriptionsQ.error).message}</MuiAlert>;

  const copy = PRODUCT_COPY[product];
  const perCuit = subscription?.features.includes('per-cuit-permissions') ?? false;
  const scopeLabel = perCuit ? 'Separación por CUIT activa' : product === 'Estudio' ? 'Acceso a la cartera del estudio' : 'Acceso al producto activo';

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3.5 },
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(82,70,255,.09), rgba(255,193,7,.08) 72%, rgba(255,255,255,.8))',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={3}>
          <Stack spacing={1.25} sx={{ maxWidth: 760 }}>
            <Typography variant="overline" color="primary.main" sx={{ letterSpacing: '.14em', fontWeight: 700 }}>{copy.eyebrow}</Typography>
            <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 750, letterSpacing: '-.035em' }}>
              Cada persona ve solo lo que necesita
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 680 }}>
              {copy.description} Esta pantalla refleja únicamente las funciones incluidas en tu producto y plan actuales.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
              <Chip color="primary" label={displayProduct} />
              <Chip variant="outlined" label={`Producto/plan ${subscription?.tierName ?? 'actual'}`} />
              <Chip variant="outlined" icon={<LockOutlined />} label={scopeLabel} />
            </Stack>
          </Stack>
          <Stack direction={{ xs: 'row', md: 'column' }} gap={1} justifyContent="center" sx={{ minWidth: 190 }}>
            <Button component={Link} to="/users" variant="contained" endIcon={<ArrowForward />}>Gestionar equipo</Button>
            <Button component={Link} to="/billing" variant="outlined">Ver producto y límites</Button>
          </Stack>
        </Stack>
      </Paper>

      <Benefits product={product} perCuit={perCuit} />
      <PlanSummary displayProduct={displayProduct} subscription={subscription} />

      {isSingleUser ? (
        <SingleUserAccess displayProduct={displayProduct} capabilities={capabilities} />
      ) : (
        <>
          {supportsCustomRoles ? (
            <Tabs value={section} onChange={(_, value: 'profiles' | 'custom') => setSection(value)} sx={{ mb: 2 }}>
              <Tab value="profiles" label="Perfiles recomendados" />
              <Tab value="custom" label="Roles a medida" />
            </Tabs>
          ) : null}
          {section === 'profiles' ? (
            <ProfilesSection product={product} capabilities={capabilities} perCuit={perCuit} />
          ) : (
            <CustomRoles product={product} capabilities={capabilities} onToast={setToast} />
          )}
        </>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">{toast?.message}</MuiAlert>
      </Snackbar>
    </Box>
  );
}

function Benefits({ product, perCuit }: { product: CommercialProduct; perCuit: boolean }): JSX.Element {
  const items = [
    {
      icon: <LockOutlined color="primary" />,
      title: product === 'Estudio' && perCuit ? 'Cada colaborador, sus CUITs' : 'Menos accesos innecesarios',
      description: product === 'Estudio' && perCuit ? 'Asigná la cartera correcta sin exponer información de otros clientes.' : 'Quien opera no necesita administrar el plan ni los permisos del equipo.',
    },
    { icon: <GroupsOutlined color="primary" />, title: 'Responsabilidades claras', description: 'Administrador, operador y consulta tienen objetivos simples y fáciles de comunicar.' },
    { icon: <WorkspacePremiumOutlined color="primary" />, title: 'Solo lo que contrataste', description: 'No aparecen módulos ni permisos de otros productos de Tilde.' },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
      {items.map((item) => (
        <Paper key={item.title} variant="outlined" sx={{ p: 2.25 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{ display: 'grid', placeItems: 'center', bgcolor: 'primary.50', borderRadius: 2, p: 1 }}>{item.icon}</Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
              <Typography variant="body2" color="text.secondary">{item.description}</Typography>
            </Box>
          </Stack>
        </Paper>
      ))}
    </Box>
  );
}

function PlanSummary({ displayProduct, subscription }: { displayProduct: string; subscription: ProductSubscription | undefined }): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
        <Box>
          <Typography variant="h6">Lo que incluye {displayProduct}</Typography>
          <Typography variant="body2" color="text.secondary">Estas son las funciones y límites que determinan los accesos disponibles.</Typography>
        </Box>
        <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap sx={{ maxWidth: 760 }}>
          {Object.entries(subscription?.limits ?? {}).map(([key, value]) => {
            const label = limitLabel(key, value);
            return label ? <Chip key={key} size="small" label={label} /> : null;
          })}
          {(subscription?.features ?? []).map((feature) => <Chip key={feature} size="small" variant="outlined" label={FEATURE_LABEL[feature] ?? feature} />)}
        </Stack>
      </Stack>
    </Paper>
  );
}

function SingleUserAccess({ displayProduct, capabilities }: { displayProduct: string; capabilities: Capability[] }): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems={{ md: 'flex-start' }}>
        <Stack spacing={1} sx={{ maxWidth: 420 }}>
          <Box sx={{ width: 52, height: 52, display: 'grid', placeItems: 'center', borderRadius: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}><Shield /></Box>
          <Typography variant="h5">Administrador de la cuenta</Typography>
          <Typography color="text.secondary">
            Tu plan {displayProduct} incluye un usuario. No necesitás configurar colaboradores: tu acceso ya cubre todas las funciones habilitadas.
          </Typography>
          <MuiAlert severity="info" variant="outlined">Si más adelante sumás equipo, podés cambiar de plan y separar quién emite, quién consulta y quién administra.</MuiAlert>
        </Stack>
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
          {capabilities.map((capability) => (
            <Stack key={capability.permission} direction="row" spacing={1.25} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <CheckCircle color="success" fontSize="small" />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 650 }}>{capability.label}</Typography>
                <Typography variant="caption" color="text.secondary">{capability.description}</Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
}

function ProfilesSection({ product, capabilities, perCuit }: { product: CommercialProduct; capabilities: Capability[]; perCuit: boolean }): JSX.Element {
  const operatorName = product === 'Estudio' ? 'Colaborador' : product === 'Api' ? 'Developer' : 'Operador';
  const definitions = PROFILE_DEFINITIONS.map((profile) => profile.key === 'operator' ? { ...profile, name: operatorName } : profile);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5">Elegí el nivel de acceso</Typography>
        <Typography color="text.secondary">Empezá por estos perfiles simples. Después podés ajustar la asignación desde Equipo.</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {definitions.map((profile) => (
          <Paper key={profile.key} variant="outlined" sx={{ p: 2.25 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ width: 44, height: 44, display: 'grid', placeItems: 'center', borderRadius: 2.5, bgcolor: `${profile.accent}.50`, color: `${profile.accent}.main` }}>{profile.icon}</Box>
                <Chip size="small" variant="outlined" label={profile.forWhom} />
              </Stack>
              <Typography variant="h6">{profile.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: 60 }}>{profile.description}</Typography>
              {product === 'Estudio' && perCuit && profile.key !== 'administrator' ? <Chip size="small" color="primary" variant="outlined" icon={<LockOutlined />} label="CUITs asignados" sx={{ alignSelf: 'flex-start' }} /> : null}
            </Stack>
          </Paper>
        ))}
      </Box>
      <Box>
        <Typography variant="h6" gutterBottom>Comparación práctica</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 300 }}>Función incluida</TableCell>
                {definitions.map((profile) => <TableCell key={profile.key} align="center">{profile.name}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {capabilities.map((capability) => (
                <TableRow key={capability.permission} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{capability.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{capability.description}</Typography>
                  </TableCell>
                  {definitions.map((profile) => {
                    const access = profileHasAccess(profile.key, capability);
                    return (
                      <TableCell key={profile.key} align="center">
                        {access === 'none' ? <Typography color="text.disabled">—</Typography> : (
                          <Tooltip title={access === 'read' ? 'Solo consulta' : 'Puede operar'}>
                            <Chip size="small" color={access === 'read' ? 'info' : 'success'} variant={access === 'read' ? 'outlined' : 'filled'} label={access === 'read' ? 'Consulta' : 'Sí'} />
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Administrar equipo y plan</Typography>
                  <Typography variant="caption" color="text.secondary">Invita personas, cambia accesos y gestiona la contratación.</Typography>
                </TableCell>
                <TableCell align="center"><Chip size="small" color="success" label="Sí" /></TableCell>
                <TableCell align="center"><Typography color="text.disabled">—</Typography></TableCell>
                <TableCell align="center"><Typography color="text.disabled">—</Typography></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
}

function CustomRoles({ product, capabilities, onToast }: { product: CommercialProduct; capabilities: Capability[]; onToast: (toast: Toast) => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<CustomRole | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CustomRole | null>(null);
  const rolesQ = useQuery({ queryKey: ['custom-roles'], queryFn: rbac.listCustomRoles });
  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: ['custom-roles'] });
  const onError = (error: unknown): void => onToast({ message: toApiError(error).message, severity: 'error' });
  const roles = (rolesQ.data ?? []).filter((role) => role.product === product);
  const deleteMut = useMutation({
    mutationFn: (id: string) => rbac.deleteCustomRole(id),
    onSuccess: () => {
      setConfirmDelete(null);
      invalidate();
      onToast({ message: 'Rol eliminado.', severity: 'success' });
    },
    onError,
  });

  if (rolesQ.isLoading) return <LoadingScreen message="Cargando roles…" />;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Roles a medida</Typography>
          <Typography color="text.secondary">Combiná únicamente permisos disponibles en este producto y plan.</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setEditing('new')}>Nuevo rol</Button>
      </Stack>
      {rolesQ.error ? <MuiAlert severity="error">{toApiError(rolesQ.error).message}</MuiAlert> : roles.length === 0 ? (
        <EmptyState
          icon={<Shield fontSize="inherit" />}
          title="Todavía no creaste roles a medida"
          description="Usalos cuando los perfiles recomendados no alcancen para separar una responsabilidad específica."
          actionLabel="Crear rol"
          onAction={() => setEditing('new')}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead><TableRow><TableCell>Rol</TableCell><TableCell>Qué puede hacer</TableCell><TableCell align="right">Acciones</TableCell></TableRow></TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{role.name}</Typography></TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                      {role.permissions.slice(0, 4).map((permission) => <Chip key={permission} size="small" variant="outlined" label={rbac.PERMISSION_LABEL[permission] ?? permission} />)}
                      {role.permissions.length > 4 ? <Chip size="small" label={`+${role.permissions.length - 4}`} /> : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditing(role)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => setConfirmDelete(role)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing ? (
        <RoleDialog
          role={editing === 'new' ? null : editing}
          product={product}
          availablePermissions={capabilities.map((capability) => capability.permission)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
            onToast({ message: 'Rol guardado.', severity: 'success' });
          }}
          onError={onError}
        />
      ) : null}
      {confirmDelete ? (
        <Dialog open onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Eliminar rol</DialogTitle>
          <DialogContent><Typography variant="body2">Se eliminará “{confirmDelete.name}”. Las personas con este rol perderán esos permisos.</Typography></DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button color="error" variant="contained" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(confirmDelete.id)}>Eliminar</Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Box>
  );
}

function RoleDialog({ role, product, availablePermissions, onClose, onSaved, onError }: {
  role: CustomRole | null;
  product: CommercialProduct;
  availablePermissions: Permission[];
  onClose: () => void;
  onSaved: () => void;
  onError: (error: unknown) => void;
}): JSX.Element {
  const isNew = role === null;
  const [name, setName] = useState(role?.name ?? '');
  const [permissions, setPermissions] = useState<Permission[]>((role?.permissions ?? []).filter((permission) => availablePermissions.includes(permission)));
  const mutation = useMutation({
    mutationFn: () => isNew
      ? rbac.createCustomRole({ product, name: name.trim(), permissions })
      : rbac.updateCustomRole(role.id, { name: name.trim(), permissions }),
    onSuccess: onSaved,
    onError,
  });
  const toggle = (permission: Permission): void => setPermissions((current) => current.includes(permission) ? current.filter((item) => item !== permission) : [...current, permission]);
  const valid = name.trim().length > 0 && permissions.length > 0;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'Nuevo rol a medida' : 'Editar rol'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField label="Nombre del rol" fullWidth value={name} onChange={(event) => setName(event.target.value)} autoFocus />
          <Box>
            <Typography variant="subtitle2" gutterBottom>Funciones permitidas ({permissions.length})</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {availablePermissions.map((permission) => (
                <FormControlLabel
                  key={permission}
                  control={<Checkbox size="small" checked={permissions.includes(permission)} onChange={() => toggle(permission)} />}
                  label={<Typography variant="body2">{rbac.PERMISSION_LABEL[permission] ?? permission}</Typography>}
                />
              ))}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? <CircularProgress size={18} /> : isNew ? 'Crear rol' : 'Guardar cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
